<template>
  <Teleport :to="INLINE_HOST_SELECTOR" :disabled="!hasInlineHost">
    <div
      class="wc-banner-alert wc-banner-alert--full-width"
      :class="[`wc-banner-alert--${variant}`, { 'wc-banner-alert--inline': hasInlineHost }]"
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      data-testid="banner"
    >
      <div class="banner-content">
        <font-awesome-icon
          :icon="variantIcon"
          class="alert-icon"
          aria-hidden="true"
          data-testid="icon"
        />
        <strong class="alert-text" data-testid="message">{{ message }}</strong>
        <button
          type="button"
          class="alert-close"
          :aria-label="$t('close')"
          @click="dismiss"
          data-testid="closeButton"
        >
          <font-awesome-icon :icon="['fas', 'xmark']" aria-hidden="true" />
        </button>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, defineProps, onMounted, onBeforeUnmount } from 'vue';
import { BANNER_VARIANT } from '@/constants/AppConstants';
import useStickyAlerts from '@/modules/alerts/composables/useStickyAlerts';
import { STICKY_ALERT_TYPE } from '@/modules/alerts/models/sticky-alert-type';
import { BannerStickyAlertPropsSchema } from '@/modules/alerts/models/banner-sticky-alert-props';

const { findAlert, answerAlert, setupTimeout } = useStickyAlerts();

const props = defineProps<BannerStickyAlertPropsSchema>();

const alert = findAlert(STICKY_ALERT_TYPE.BANNER, props.id);

const dismiss = () => {
  answerAlert(alert, { result: 'DISMISS' });
};

setupTimeout(STICKY_ALERT_TYPE.BANNER, props.id, alert, props.options?.timeoutMs);

const variantIcon = computed(() => {
  const map: Record<string, string[]> = {
    [BANNER_VARIANT.WARNING]: ['fas', 'triangle-exclamation'],
    [BANNER_VARIANT.DANGER]: ['fas', 'circle-exclamation'],
    [BANNER_VARIANT.SUCCESS]: ['fas', 'circle-check'],
    [BANNER_VARIANT.INFO]: ['fas', 'circle-info'],
  };
  return map[props.variant] ?? map[BANNER_VARIANT.WARNING];
});

const BANNER_ANCHOR_SELECTOR = '#desktop-header-v2, .wc-mobile-subpage-header';
const STICKY_ALERT_ROOT_SELECTOR = '.sticky-alert-root';
const BANNER_TOP_VAR = '--wc-banner-top';

// A modal that owns its own alert area renders this host, and the banner moves inside it
// rather than floating over the page behind the modal. When no host is on the page the
// teleport is disabled and the banner keeps its usual fixed position under the header.
const INLINE_HOST_SELECTOR = '#wc-banner-host';
const hasInlineHost = Boolean(document.querySelector(INLINE_HOST_SELECTOR));

const reposition = (): void => {
  const anchors = Array.from(document.querySelectorAll(BANNER_ANCHOR_SELECTOR));
  const anchorBottom = Math.max(0, ...anchors.map(anchor => anchor.getBoundingClientRect().bottom));
  const root = document.querySelector(STICKY_ALERT_ROOT_SELECTOR);
  const rootTop = root?.getBoundingClientRect().top ?? 0;
  document.documentElement.style.setProperty(BANNER_TOP_VAR, `${anchorBottom - rootTop}px`);
};

onMounted(() => {
  if (hasInlineHost) {
    return;
  }
  reposition();
  window.addEventListener('resize', reposition);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', reposition);
});
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.wc-banner-alert {
  box-sizing: border-box;
  padding: 0 convert-px-to-rem(15);
  display: block;
}

.wc-banner-alert:not(.wc-banner-alert--inline) {
  position: fixed !important;
  top: calc(var(--wc-banner-top, 0px) + 0.75rem) !important;
  left: 50% !important;
  width: 100vw !important;
  margin: 0 !important;
  transform: translateX(-50%) !important;
}

/* Inside a modal's own alert area the banner is part of the layout, not an overlay. */
.wc-banner-alert--inline {
  width: 100%;
  padding: 0;
}

.wc-banner-alert--full-width {
  .banner-content {
    display: flex;
    align-items: center;
    gap: convert-px-to-rem(10);
    padding: convert-px-to-rem(10) convert-px-to-rem(14);
    font-size: 0.875rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    border-radius: 0.375rem;
    color: var(--black, $black);
  }
}

.wc-banner-alert--warning {
  .banner-content {
    background: var(--warning-lighter-9, get-color('warning', 'lighter-9'));
  }
  .alert-icon {
    color: var(--warning-darker-3, get-color('warning'));
  }
}

.wc-banner-alert--danger {
  .banner-content {
    background: var(--danger-lighter-9, get-color('danger', 'lighter-9'));
  }
  .alert-icon {
    color: var(--danger, get-color('danger'));
  }
}

.wc-banner-alert--success {
  .banner-content {
    background: var(--success-lighter-9, get-color('success', 'lighter-9'));
  }
  .alert-icon {
    color: var(--success, get-color('success'));
  }
}

.wc-banner-alert--info {
  .banner-content {
    background: var(--info-lighter-9, get-color('info', 'lighter-9'));
  }
  .alert-icon {
    color: var(--info, get-color('info'));
  }
}

.alert-icon {
  flex-shrink: 0;
}

.alert-text {
  flex: 1;
  font-weight: 400;
  line-height: 1.4;
  color: var(--dark, $dark);
  font-size: $font-size-sm;
}

.alert-close {
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--dark-lighter-4, get-color('dark', 'lighter-4'));
  padding: convert-px-to-rem(2);
  line-height: 1;

  &:hover {
    color: var(--dark, get-color('dark'));
  }
}
</style>
